import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  BookOpen,
  Users,
  Award,
  Play,
} from "lucide-react";
import { useAuth } from "../App";

const HomePage = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight text-[#0f3e3e]">
                Professional Safety Training <br />
                <span className="text-[#1a1a1a]">
                  Build a Safe & Secure Career
                </span>
              </h1>
              <p className="text-lg text-muted-foreground mt-6">
                Safety Training Institute, Bahanaga provides industry-oriented
                safety training and certification for construction,
                manufacturing, logistics, and high-risk workplaces.
              </p>
              <div className="flex gap-4 mt-8">
                <Link to="/courses">
                  <Button className="bg-[#0f3e3e] text-white h-12 px-8">
                    View Courses <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                {!user && (
                  <Link to="/contact">
                    <Button variant="outline" className="h-12 px-8">
                      Enquire Now
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            <div className="relative">
              <img
                src="https://img.freepik.com/premium-vector/cartoon-vector-illustration-business-people-working-office-with-man-using-computer_1350781-624.jpg?semt=ais_hybrid&w=740&q=80"
                alt="Safety Training"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#d4ff32] text-[#0f3e3e] p-6 rounded-lg shadow-lg">
                <p className="text-3xl font-bold">500+</p>
                <p className="text-sm font-medium">Students Trained</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#f8f9fa]">
        <div className="max-w-7xl mx-auto text-center mb-14">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#0f3e3e]">
            Why Choose Safety Training Institute?
          </h2>
          <p className="text-muted-foreground mt-3">
            Practical, affordable & career-focused safety education
          </p>
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          <div className="bg-white p-6 rounded-lg border">
            <BookOpen className="w-10 h-10 text-[#0f3e3e] mb-4" />
            <h3 className="text-xl font-medium mb-2">
              Expert Trainers
            </h3>
            <p className="text-muted-foreground">
              Learn from experienced safety professionals with real industry
              exposure.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <Users className="w-10 h-10 text-[#0f3e3e] mb-4" />
            <h3 className="text-xl font-medium mb-2">
              Practical Training
            </h3>
            <p className="text-muted-foreground">
              Hands-on sessions, live demonstrations & real-world scenarios.
            </p>
          </div>

          <div className="bg-white p-6 rounded-lg border">
            <Award className="w-10 h-10 text-[#0f3e3e] mb-4" />
            <h3 className="text-xl font-medium mb-2">
              Certification
            </h3>
            <p className="text-muted-foreground">
              Industry-recognized certificates after successful completion.
            </p>
          </div>
        </div>
      </section>

      {/* Institute Approved By */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#0f3e3e] mb-10">
            Institute Approved By
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              "Industrial Safety Standards",
              "Construction Safety Guidelines",
              "Workplace Safety Authorities",
              "Industry Experts",
            ].map((item, i) => (
              <div key={i} className="border rounded-lg p-6">
                <Award className="w-8 h-8 mx-auto text-[#0f3e3e] mb-3" />
                <p className="text-sm text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Student Reviews */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-[#f8f9fa]">
        <div className="max-w-6xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold text-[#0f3e3e]">
            Student Reviews
          </h2>
        </div>

        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            {
              name: "Rahul Kumar",
              review:
                "Practical training helped me get job-ready skills. Trainers explained safety concepts very clearly.",
            },
            {
              name: "Amit Das",
              review:
                "Best safety training institute in Balasore. The practical sessions were very useful.",
            },
            {
              name: "Suresh Nayak",
              review:
                "Affordable fees and excellent trainers. I gained confidence to work at industrial sites.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="bg-white border p-6 rounded-lg hover:shadow-lg transition"
            >
              <p className="text-muted-foreground mb-4">
                “{item.review}”
              </p>
              <p className="font-medium text-[#0f3e3e]">
                – {item.name}
              </p>
            </div>
          ))}
        </div>
      </section>


      {/* CTA Section */} <section className="py-20 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0f3e3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="cta-title"> Ready to Start Learning? </h2>
          <p className="text-lg md:text-xl leading-relaxed opacity-90 mb-8"> Join thousands of students already learning on Safety Training Institute </p>
          <Link to="/courses">
          <Button className="bg-[#d4ff32] text-[#0f3e3e] hover:bg-[#d4ff32]/90 h-12 px-8 rounded-md font-medium text-lg" data-testid="cta-button" > Browse Courses <Play className="ml-2 h-5 w-5" /> </Button> </Link>
          </div>
          </section>

      {/* FAQ */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl text-center font-semibold text-[#0f3e3e] mb-10">
            Frequently Asked Questions
          </h2>

          {[
            [
              "Do you provide certificates?",
              "Yes, certificates are provided after course completion.",
            ],
            [
              "Is practical training included?",
              "Yes, all courses include hands-on practical sessions.",
            ],
            [
              "Do you help with job guidance?",
              "Yes, we provide career support and guidance.",
            ],
          ].map(([q, a], i) => (
            <details key={i} className="border rounded-lg p-5 mb-4">
              <summary className="font-medium text-[#0f3e3e] cursor-pointer">
                {q}
              </summary>
              <p className="text-muted-foreground mt-3">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Location */}
      <iframe
        title="Location"
        src="https://www.google.com/maps?q=Bahanaga%20Balasore%20Odisha&output=embed"
        className="w-full h-100 rounded-lg border"
        loading="lazy" style={{
    marginBottom: "-5em",height: "500px",
  }}
      />

      <Footer />
    </div>
  );
};

export default HomePage;